import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import { get_pv, post_pv, get_tolerance_master } from './api';

const initialState = {
  loading: false,
  batchesLoading: false,
  processesLoading: false,
  containersLoading: false,
  submitting: false,
  previousBatches: [],
  derivedPreviousBatchNumber: '',
  packingBatchNumber: '',
  currentStage: null,
  processes: [],
  containers: [],
  persistedBalanceName: null,
  tolerance: null,
  anchor: null,
  snapshotSource: null,
  snapshotCapturedAt: null
};

const actions = {
  GET_PREVIOUS_BATCHES: 'productVerification/GET_PREVIOUS_BATCHES',
  GET_PROCESSES: 'productVerification/GET_PROCESSES',
  GET_CONTAINERS: 'productVerification/GET_CONTAINERS',
  SCAN_RESOLVE: 'productVerification/SCAN_RESOLVE',
  SUBMIT_SPLIT: 'productVerification/SUBMIT_SPLIT',
  GENERATE_PDF: 'productVerification/GENERATE_PDF',
  GET_TOLERANCE: 'productVerification/GET_TOLERANCE'
};

export const getPreviousStageBatches = createAsyncThunk(
  actions.GET_PREVIOUS_BATCHES,
  async (payload) => {
    const path = payload?.lc_bmr_eqp_id
      ? `previous-stage-batches/lc/${payload.lc_bmr_eqp_id}`
      : `previous-stage-batches/${payload.dispensing_request_id}`;
    const response = await get_pv(path);
    return response;
  }
);

export const getProcessesForBatch = createAsyncThunk(
  actions.GET_PROCESSES,
  async (payload) => {
    const response = await get_pv('processes', payload);
    return response;
  }
);

export const getContainersForVerification = createAsyncThunk(
  actions.GET_CONTAINERS,
  async (payload) => {
    const response = await get_pv('containers', payload);
    return response;
  }
);

export const scanResolveContainer = createAsyncThunk(
  actions.SCAN_RESOLVE,
  async (payload) => {
    const response = await post_pv('scan-resolve', payload);
    return response;
  }
);

export const submitSplit = createAsyncThunk(
  actions.SUBMIT_SPLIT,
  async (payload) => {
    const response = await post_pv('submit-split', payload);
    return response;
  }
);

export const generateVerificationPdf = createAsyncThunk(
  actions.GENERATE_PDF,
  async (payload) => {
    const response = await get_pv('generate-pdf', payload);
    return response;
  }
);

export const getVerificationTolerance = createAsyncThunk(
  actions.GET_TOLERANCE,
  async (payload) => {
    const response = await get_tolerance_master(payload);
    return response;
  }
);

export const productVerificationSlice = createSlice({
  name: 'productVerification',
  initialState,
  reducers: {
    resetProductVerification: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPreviousStageBatches.pending, (state) => {
        state.batchesLoading = true;
      })
      .addCase(getPreviousStageBatches.fulfilled, (state, action) => {
        state.batchesLoading = false;
        const {
          success,
          data,
          message: msg,
          source,
          captured_at
        } = action.payload || {};
        if (success && data) {
          state.previousBatches = data.batches || [];
          state.derivedPreviousBatchNumber =
            data.derived_previous_batch_number || '';
          state.packingBatchNumber = data.packing_batch_number || '';
          state.currentStage = data.current_stage || null;
          state.anchor = data.anchor || null;
          state.snapshotSource = source || null;
          state.snapshotCapturedAt = captured_at || null;
          if ((data.batches || []).length === 0) {
            message.warning('WIP Labels are NOT YET generated');
          }
        } else {
          state.previousBatches = [];
          state.currentStage = null;
          state.anchor = null;
          state.snapshotSource = null;
          state.snapshotCapturedAt = null;
          if (msg) message.error(msg);
        }
      })
      .addCase(getPreviousStageBatches.rejected, (state, action) => {
        state.batchesLoading = false;
        state.previousBatches = [];
        state.snapshotSource = null;
        state.snapshotCapturedAt = null;
        message.error(
          action.error?.message || 'Failed to load previous stage batches'
        );
      });

    builder
      .addCase(getProcessesForBatch.pending, (state) => {
        state.processesLoading = true;
      })
      .addCase(getProcessesForBatch.fulfilled, (state, action) => {
        state.processesLoading = false;
        const { success, data, message: msg } = action.payload || {};
        if (success) {
          state.processes = data || [];
        } else {
          state.processes = [];
          if (msg) message.error(msg);
        }
      })
      .addCase(getProcessesForBatch.rejected, (state, action) => {
        state.processesLoading = false;
        state.processes = [];
        message.error(action.error?.message || 'Failed to load processes');
      });

    builder
      .addCase(getContainersForVerification.pending, (state) => {
        state.containersLoading = true;
      })
      .addCase(getContainersForVerification.fulfilled, (state, action) => {
        state.containersLoading = false;
        const { success, data, meta, message: msg } = action.payload || {};
        if (success) {
          state.containers = data || [];
          state.persistedBalanceName = meta?.persisted_balance_name || null;
        } else {
          state.containers = [];
          state.persistedBalanceName = null;
          if (msg) message.error(msg);
        }
      })
      .addCase(getContainersForVerification.rejected, (state, action) => {
        state.containersLoading = false;
        state.containers = [];
        message.error(action.error?.message || 'Failed to load containers');
      });

    builder
      .addCase(getVerificationTolerance.fulfilled, (state, action) => {
        const { success, data } = action.payload || {};
        if (success && data) {
          state.tolerance = data;
        } else {
          state.tolerance = null;
        }
      })
      .addCase(getVerificationTolerance.rejected, (state) => {
        state.tolerance = null;
      });

    builder
      .addCase(submitSplit.pending, (state) => {
        state.submitting = true;
      })
      .addCase(submitSplit.fulfilled, (state, action) => {
        state.submitting = false;
        const { success, message: msg } = action.payload || {};
        if (success) {
          message.success(msg || 'Verification recorded successfully.');
        } else {
          message.error(msg || 'Failed to record verification.');
        }
      })
      .addCase(submitSplit.rejected, (state, action) => {
        state.submitting = false;
        message.error(
          action.error?.message || 'Failed to record verification.'
        );
      });
  }
});

export const { resetProductVerification } = productVerificationSlice.actions;
export default productVerificationSlice.reducer;
